import type {DotLottie} from '@lottiefiles/dotlottie-react'
import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import {isFilled} from '@prismicio/client'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {HeroV3SliceAnimatedBackground} from 'prismicio-types'
import {useCallback, useEffect, useRef, useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {useProgressBarRef} from 'slices/Carousel/useProgressBarRef'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {LeadIdProvider} from 'slices/ProductTourSection/context/LeadIdContext'
import {getResolvedLink} from 'src/common/helpers'
import {LeadGenerationField} from 'src/common/LeadGenerationField'
import type {Context} from 'src/common/types'
import {AnimatedBackground} from '../components/AnimatedBackground'

type AnimatedBackgroundVariationProps = {
  slice: HeroV3SliceAnimatedBackground & {
    slice_type: string
    variation: string
  }
  context: Context
  componentIndex: number
}

export const HeroAnimatedLinesBackground = ({
  slice,
  context: {pageUid, buttonsData},
}: AnimatedBackgroundVariationProps) => {
  const router = useRouter()
  const {linkResolver} = useLinkResolver()
  const inputField = slice.primary.input_field[0]
  const resolvedSubmitLink = getResolvedLink({link: inputField?.submit_link, buttonsData})
  const successLink = isFilled.link(resolvedSubmitLink) ? linkResolver(resolvedSubmitLink, 'hero-v3') : null

  const animations =
    (isFilled.group(slice.primary.assets) &&
      slice.primary.assets.filter((asset) => isFilled.link(asset.lottie_animation) && asset.lottie_animation.url)) ||
    []

  const [activeIndex, setActiveIndex] = useState(0)
  const dotLottieRef = useRef<DotLottie | null>(null)
  const {progressBarRef, updateProgress} = useProgressBarRef()

  const activeAnimationSrc =
    (isFilled.link(animations[activeIndex]?.lottie_animation) && animations[activeIndex].lottie_animation.url) || ''

  const handleSubmitSuccess = useCallback(() => {
    if (successLink) {
      router.push(successLink)
    }
  }, [router, successLink])

  const handleAnimationComplete = useCallback(() => {
    // Move to next animation when current one finishes
    setActiveIndex((prevIndex) => (prevIndex + 1) % animations.length)
    // Progress will reset automatically when animation restarts at frame 0
  }, [animations.length])

  // Set up event listeners with proper cleanup
  useEffect(() => {
    const dotLottie = dotLottieRef.current

    if (!dotLottie) {
      return
    }

    const handleFrame = () => {
      const currentFrame = dotLottie.currentFrame
      const totalFrames = dotLottie.totalFrames

      if (totalFrames > 0) {
        const progressValue = currentFrame / totalFrames
        // useProgressBarRef uses scaleX(1 - progress), so we invert here to get scaleX(progress)
        updateProgress(1 - progressValue)
      }
    }

    const handleComplete = () => {
      handleAnimationComplete()
    }

    dotLottie.addEventListener('frame', handleFrame)
    dotLottie.addEventListener('complete', handleComplete)

    // Cleanup listeners when animation changes or component unmounts
    return () => {
      dotLottie.removeEventListener('frame', handleFrame)
      dotLottie.removeEventListener('complete', handleComplete)
    }
  }, [activeIndex, handleAnimationComplete, updateProgress])

  const handleDotClick = useCallback((index: number) => {
    setActiveIndex(index)

    // Restart the animation when manually switching
    if (dotLottieRef.current) {
      dotLottieRef.current.setFrame(0)
      dotLottieRef.current.play()
    }
    // Progress will reset automatically when animation restarts at frame 0
  }, [])

  return (
    <LeadIdProvider>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative overflow-hidden bg-green-dark pb-5 md:pb-8"
        data-nav-dark="true"
      >
        <div className="max-md:hidden">
          <AnimatedBackground />
        </div>

        {/* Hero Content */}
        <div
          className="relative z-10 mx-auto flex w-full max-w-[1200px] flex-col items-center gap-6 pt-[116px] md:gap-20 md:pt-[148px]"
          data-aos="fade-up"
          data-aos-offset="0"
        >
          <div className="flex flex-col items-center gap-6 px-5 md:gap-8 md:px-10 lg:px-5 xl:px-0">
            {/* Eyebrow */}
            <p className="text-button-xs md:text-button whitespace-pre text-center uppercase text-moss-white">
              {slice.primary.eyebrow}
            </p>

            {/* Main Headline */}
            <h1 className="whitespace-pre text-wrap text-center text-40 font-medium leading-[42px] tracking-[-.4px] text-moss-white md:text-[80px] md:leading-normal">
              {slice.primary.heading}
            </h1>

            {/* Description */}
            <div className="text-tg-body-l md:text-body-xl w-full max-w-[638px] text-center font-medium text-moss-white/80">
              <p>{slice.primary.description}</p>
            </div>

            {/* Email Input with CTA */}
            <LeadGenerationField
              pageUid={pageUid}
              placeholder={slice.primary.input_field[0]?.placeholder}
              submitButtonText={slice.primary.input_field[0]?.submit_button_label}
              onSuccess={handleSubmitSuccess}
              tracking={{
                recaptchaAction: 'hero_lead',
                formName: 'hero-lead-form',
                intent: 'hero-lead',
                location: 'hero-section',
              }}
            />
          </div>
          {/* AI Assistant Card */}
          <div className="flex flex-col gap-6 md:gap-20">
            {animations.length > 0 && (
              <div className="relative h-[370px] w-screen overflow-hidden rounded-[24px] md:w-[679px]">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={activeIndex}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.6, ease: 'easeInOut'}}
                    className="absolute inset-0"
                  >
                    <DotLottieReact
                      src={activeAnimationSrc}
                      autoplay
                      dotLottieRefCallback={(dotLottie: DotLottie) => {
                        dotLottieRef.current = dotLottie
                      }}
                      renderConfig={{
                        autoResize: true,
                        devicePixelRatio: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
                      }}
                      style={{width: '100%', height: '100%'}}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            )}
            {/* Navigation */}
            {animations.length > 1 && (
              <div className="flex items-center justify-center gap-2">
                {animations.map((_, index) => (
                  <button
                    key={`nav-dot-${index}`}
                    onClick={() => handleDotClick(index)}
                    className={cn(
                      'relative h-2 overflow-hidden rounded-[1px] bg-moss-white/30 transition-all duration-300 hover:bg-moss-white/50',
                      {
                        'w-4': index !== activeIndex,
                        'w-8': index === activeIndex,
                      },
                    )}
                    aria-label={`Go to animation ${index + 1}`}
                  >
                    {index === activeIndex && (
                      <div
                        ref={progressBarRef}
                        className="absolute inset-0 origin-left bg-moss-white"
                        style={{transform: 'scaleX(0)'}}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </LeadIdProvider>
  )
}
